import { fortschritt } from './bauordnungLogic';
import type { Dokumentart } from './kapitel';
import type { Bauordnung, BauordnungBundle } from './types';
import type { ProjektMerkmale } from '../planungsphase/types';

/**
 * Der Stand der Baustellenordnung als EINE Zeile auf der Hub-Kachel 06
 * (docs/baustellenordnung-assistenz-plan.md, Stufe B4) — „Entwurf · 4 von 9
 * Kapiteln", „Freigegeben Rev. 01 · 01.11.2026", „noch keine
 * Baustellenordnung". Muster ist die Statuszeile der Planungskachel
 * (planungKachel.ts): Text und Ton entscheidet diese reine Funktion, die
 * Komponente zeichnet nur.
 *
 * Fail-open wie `fetchBauordnungBundle`: ein Stand, der nicht gelesen oder
 * nicht gerechnet werden konnte, heisst „nicht lesbar" — nie gruen und nie
 * „noch keine", denn beides waere eine Behauptung ohne Daten.
 *
 * Ohne React, ohne Supabase.
 */

export type KachelTon = 'grau' | 'blau' | 'amber' | 'gruen' | 'fehler';

export interface BauordnungKachelText {
    text: string;
    ton: KachelTon;
}

/** Was `useBauordnungKachel` liefert — hier definiert, damit die reine Seite den Hook nicht braucht. */
export interface BauordnungKachelLage {
    laden: boolean;
    bundle: BauordnungBundle | null;
    /** Lesefehler im Klartext — dann ist `bundle` null. */
    fehler: string | null;
}

/** Was die Kachel bekommt: solange gelesen wird, kein Text. */
export interface BauordnungKachelAnzeige {
    laden: boolean;
    text: BauordnungKachelText | null;
}

export const KACHEL_NICHT_LESBAR = 'Stand nicht lesbar';
export const KACHEL_SCHEMA_FEHLT = 'Datenbank kennt das Modul noch nicht';
export const KACHEL_KEINE = 'noch keine Baustellenordnung';

/**
 * Die Dokumente, deren Kapitel der Entwurf zaehlt — dieselbe Auswahl wie
 * `freigabeHindernisse`: was der Kopfsatz erzeugt. Ist keins gewaehlt (das
 * hindert die Freigabe ohnehin), zaehlt die Baustellenordnung, damit die
 * Zeile nicht „0 von 0" behauptet.
 */
const gezaehlteDokumente = (o: Pick<Bauordnung, 'erzeugt_ordnung' | 'erzeugt_hse'>): Dokumentart[] => {
    const arten: Dokumentart[] = [];
    if (o.erzeugt_ordnung) arten.push('ordnung');
    if (o.erzeugt_hse) arten.push('hse');
    return arten.length ? arten : ['ordnung'];
};

const zweistellig = (n: number): string => String(n).padStart(2, '0');

/**
 * TT.MM.JJJJ. Ein reiner Kalendertag wird zerlegt (er darf nicht wandern);
 * ein Zeitpunkt — `freigegeben_am` ist `timestamptz` und kommt als UTC
 * zurueck — wird als ORTSZEIT gelesen, sonst stuende eine Freigabe kurz
 * nach Mitternacht mit dem Vortag auf der Kachel. Unlesbares wird leer.
 */
export const kachelDatum = (wert: string | null | undefined): string => {
    const roh = String(wert ?? '').trim();
    if (!roh) return '';
    const tag = /^(\d{4})-(\d{2})-(\d{2})$/.exec(roh);
    if (tag) return `${tag[3]}.${tag[2]}.${tag[1]}`;
    const z = new Date(roh);
    if (Number.isNaN(z.getTime())) return '';
    return `${zweistellig(z.getDate())}.${zweistellig(z.getMonth() + 1)}.${z.getFullYear()}`;
};

const freigabeText = (o: Bauordnung): string => {
    const fassung = String(o.version_label ?? '').trim();
    const tag = kachelDatum(o.freigegeben_am);
    const kopf = fassung ? `Freigegeben ${fassung}` : 'Freigegeben';
    return tag ? `${kopf} · ${tag}` : kopf;
};

const entwurfText = (bundle: BauordnungBundle, o: Bauordnung, merkmale: ProjektMerkmale): BauordnungKachelText => {
    let fertig = 0;
    let gesamt = 0;
    for (const d of gezaehlteDokumente(o)) {
        const f = fortschritt(bundle, d, merkmale);
        fertig += f.fertig;
        gesamt += f.gesamt;
    }
    // Nur PFLICHT-Punkte: genau die halten die Freigabe auf (freigabeHindernisse).
    const offen = bundle.luecken.filter(l => l.pflicht && !l.erledigt_am).length;
    const kapitel = `Entwurf · ${fertig} von ${gesamt} ${gesamt === 1 ? 'Kapitel' : 'Kapiteln'}`;
    if (!offen) return { text: kapitel, ton: 'blau' };
    const punkte = offen === 1 ? '1 offener Punkt' : `${offen} offene Punkte`;
    return { text: `${kapitel} · ${punkte}`, ton: 'amber' };
};

/**
 * Die Statuszeile der Kachel 06.
 *
 * Reihenfolge der Faelle: Lesefehler, fehlendes Schema, kein Kopfsatz,
 * freigegeben, Entwurf. Scheitert die Rechnung an einer kaputten Zeile,
 * wird daraus „nicht lesbar" — die Kachel darf am Stand nicht scheitern.
 */
export const bauordnungKachelText = (
    bundle: BauordnungBundle | null,
    merkmale: ProjektMerkmale,
    fehler: string | null,
): BauordnungKachelText => {
    if (fehler || !bundle) return { text: KACHEL_NICHT_LESBAR, ton: 'fehler' };
    if (bundle.schemaFehlt) return { text: KACHEL_SCHEMA_FEHLT, ton: 'fehler' };
    const o = bundle.ordnung;
    if (!o) return { text: KACHEL_KEINE, ton: 'grau' };
    if (o.status === 'freigegeben') return { text: freigabeText(o), ton: 'gruen' };
    try {
        return entwurfText(bundle, o, merkmale);
    } catch (e) {
        console.warn('Stand der Baustellenordnung konnte nicht berechnet werden.', e);
        return { text: KACHEL_NICHT_LESBAR, ton: 'fehler' };
    }
};

/** Lage des Hooks → Anzeige der Kachel; solange gelesen wird, ohne Text. */
export const bauordnungKachelAnzeige = (
    lage: BauordnungKachelLage,
    merkmale: ProjektMerkmale,
): BauordnungKachelAnzeige => (lage.laden
    ? { laden: true, text: null }
    : { laden: false, text: bauordnungKachelText(lage.bundle, merkmale, lage.fehler) });
